import { useQuery } from "@tanstack/react-query";

import {
  PCRS_STATUS,
  PRIVATE_FINANCIAL_CLASS,
} from "features/cdm/constants/pcrsSchemeCheck";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePatientHealthIdentifiers } from "features/patients/hooks/usePatientHealthIdentifiers";
import { isPcrsExtensionInstalled, sendToExtension } from "features/pcrsExtension";

// The PCRS scheme check no longer goes through our backend — the Kinesin PCRS
// browser extension performs the mTLS SOAP call to PCRS directly. The endpoint,
// site identifier and (non-secret) vendor token are environment-specific.
const PCRS_ENDPOINT = process.env.REACT_APP_SSPCRS_ENDPOINT;
const PCRS_VENDOR_ID = process.env.REACT_APP_SSPCRS_VENDOR_ID;
const PCRS_SITE_ID = process.env.REACT_APP_SSPCRS_SITE_ID;

const XML_INSTANCE_NS = "http://www.w3.org/2001/XMLSchema-instance";

// PCRS replies with a SOAP envelope; flatten its <result> children into an
// object, mapping xsi:nil="true" elements to null.
const parseSchemeCheckResult = (soapXml) => {
  const document = new DOMParser().parseFromString(soapXml, "application/xml");
  const result = document.getElementsByTagName("result")[0];
  if (!result) return null;

  const fields = {};
  for (const child of result.children) {
    const isNil = child.getAttributeNS(XML_INSTANCE_NS, "nil") === "true";
    fields[child.localName] = isNil ? null : child.textContent.trim();
  }

  return fields;
};

const usePCRSSchemeCheckReal = ({ patientId }) => {
  const { data: patient } = usePatientData(patientId);
  const { data: healthIdentifiers, isLoading: isHealthIdentifiersLoading } =
    usePatientHealthIdentifiers(patientId);

  const isPrivate = patient?.financialClass === PRIVATE_FINANCIAL_CLASS;
  // PCRS keys the scheme check on the patient's card number. When the patient
  // has an HAA number, use it as-is in preference to the GMS/medical card.
  const haaNumber = healthIdentifiers?.haaNumber;
  const schemeId = haaNumber || patient?.medicalCardNumber;

  // siteId is the site prefix (e.g. "KINESIN") joined with the tenant,
  // e.g. "KINESIN_<tenant>".
  const tenant = localStorage.getItem("kinesin-tenant");
  const siteId = `${PCRS_SITE_ID}_${tenant}`;

  return useQuery({
    queryKey: ["pcrsSchemeCheck", patientId, schemeId, isPrivate],
    queryFn: async () => {
      // Private patients have no GMS entitlements — skip the call, but still
      // allow CDM (mirrors the previous backend behaviour).
      if (isPrivate) {
        return {
          status: PCRS_STATUS.PRIVATE_SKIPPED,
          validPatient: true,
          schemeCheckPerformed: false,
        };
      }

      if (!schemeId) {
        return {
          status: PCRS_STATUS.NO_MEDICAL_CARD,
          validPatient: false,
        };
      }

      // Before any PCRS call: without the extension the page shows install
      // instructions instead of a failed check.
      if (!(await isPcrsExtensionInstalled())) {
        return {
          status: PCRS_STATUS.EXTENSION_MISSING,
          validPatient: false,
        };
      }

      const message = {
        operation: "checkClientNew",
        endpoint: PCRS_ENDPOINT,
        params: {
          guid: crypto.randomUUID(),
          vendorID: PCRS_VENDOR_ID,
          schemeId,
          siteId,
        },
      };

      let response;
      try {
        response = await sendToExtension(message);
      } catch (error) {
        // The extension answered the ping, so this is a failed call, not a
        // missing extension.
        console.error("[PCRS scheme check] extension call failed:", error);

        return {
          status: PCRS_STATUS.CONNECTION_ERROR,
          validPatient: false,
          error: error.message,
        };
      }

      if (!response?.ok) {
        // The plugin passes the real failure straight through — log the whole
        // payload so the actual error/name/status is visible, not a stray
        // "undefined".
        console.error("[PCRS scheme check] call failed:", response);

        return {
          status: PCRS_STATUS.CONNECTION_ERROR,
          validPatient: false,
          error: response?.error ?? null,
        };
      }

      const fields = parseSchemeCheckResult(response.body);
      const validPatient = fields?.patientFound === "true";

      return {
        ...fields,
        status: validPatient ? PCRS_STATUS.VALID : PCRS_STATUS.INVALID,
        validPatient,
        invalidReason: fields?.invalidReason ?? null,
        schemeCheckPerformed: true,
        // Keep the raw SOAP body so it can be posted to the BE for authoritative
        // persistence/audit (it re-parses rather than trusting the FE parse).
        rawXml: response.body ?? null,
        parsedResult: fields ?? null,
      };
    },
    // Wait for the health identifiers so we don't run the check with the GMS
    // number before a present HAA number has loaded.
    enabled: !!patient && !isHealthIdentifiersLoading,
  });
};

// Dev-only: when REACT_APP_CDM_MOCK_PCRS=true the PCRS scheme check is mocked as
// valid (no extension call) so CDM can be worked on without the PCRS bridge.
// Default off; the real implementation is preserved above.
const MOCK_PCRS = process.env.REACT_APP_CDM_MOCK_PCRS === "true";

export const usePCRSSchemeCheck = MOCK_PCRS
  ? () => ({
      data: {
        status: PCRS_STATUS.VALID,
        validPatient: true,
        schemeCheckPerformed: true,
        invalidReason: null,
      },
      isLoading: false,
      isFetching: false,
      isError: false,
      refetch: () => {},
    })
  : usePCRSSchemeCheckReal;
